import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Buildings, Receipt, Car, ClipboardText, Percent, CalendarBlank, CarProfile, ForkKnife, Truck } from "@phosphor-icons/react";
import CompanyTab from "./CompanyTab";
import PreTradingTab from "./PreTradingTab";
import MileageTab from "./MileageTab";
import ERRTab from "./ERRTab";
import VatTab from "./VatTab";
import CalendarTab from "./CalendarTab";
import UkMileageTab from "./UkMileageTab";
import UkSubsistenceTab from "./UkSubsistenceTab";
import UsMileageTab from "./UsMileageTab";

const TABS = [
  { id: "setup", label: "Company Setup", icon: Buildings },
  { id: "calendar", label: "Calendar", icon: CalendarBlank },
  { id: "pretrading", label: "Pre-Trading (S.82)", icon: Receipt },
  { id: "vat", label: "VAT", icon: Percent },
  { id: "mileage", label: "IE Mileage", icon: Car },
  { id: "err", label: "IE ERR", icon: ClipboardText },
  { id: "uk_mileage", label: "UK Mileage", icon: CarProfile },
  { id: "uk_subsistence", label: "UK Subsistence", icon: ForkKnife },
  { id: "us_mileage", label: "US Mileage", icon: Truck },
];

export default function Compliance() {
  const [tab, setTab] = useState("setup");
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    try { const r = await api.get("/compliance/summary"); setSummary(r.data); }
    catch (err) { setSummary(null); }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Irish Statutory Compliance</div>
        <h1 className="font-display text-4xl">Revenue &amp; ERR compliance</h1>
        <p className="text-white/60 text-sm mt-2">
          Pre-Trading Expenses (S.82 TCA 1997) · Civil Service mileage (2025) · Enhanced Reporting Requirements.
        </p>
      </div>

      {summary?.cro && <ReminderBanner title={`CRO Annual Return #${summary.cro.return_number}`} item={summary.cro} onManage={() => setTab("setup")} testid="cro-banner" />}
      {summary?.vat && <ReminderBanner title={`VAT return (${summary.vat.period_label})`} item={summary.vat} onManage={() => setTab("vat")} testid="vat-banner" />}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="compliance-summary">
          <SummaryTile label="Pre-Trading eligible" value={`€${Number(summary.pretrading.eligible_total).toLocaleString()}`} />
          <SummaryTile label="Mileage YTD" value={`€${Number(summary.mileage.ytd_euro).toLocaleString()}`} hint={`${Number(summary.mileage.ytd_km).toLocaleString()} km`} />
          <SummaryTile label="Small Benefit left" value={`€${Number(summary.err.small_benefit_remaining).toLocaleString()}`} hint={`${summary.err.small_benefit_count}/${summary.err.small_benefit_max_count} used`} warn={summary.err.small_benefit_over_limit || summary.err.small_benefit_over_count} />
          <SummaryTile label="Unreported to ROS" value={summary.mileage.unreported_count + summary.err.unreported_count} warn={(summary.mileage.unreported_count + summary.err.unreported_count) > 0} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-white/5 pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`compliance-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm transition-colors ${tab === t.id ? "bg-white/8 text-white border-b-2 border-[#FFB020]" : "text-white/55 hover:text-white/85"}`}
          >
            <t.icon size={16} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        {tab === "setup" && <CompanyTab onSaved={loadSummary} />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "pretrading" && <PreTradingTab onChange={loadSummary} />}
        {tab === "vat" && <VatTab onChange={loadSummary} />}
        {tab === "mileage" && <MileageTab onChange={loadSummary} />}
        {tab === "err" && <ERRTab onChange={loadSummary} />}
        {tab === "uk_mileage" && <UkMileageTab onChange={loadSummary} />}
        {tab === "uk_subsistence" && <UkSubsistenceTab onChange={loadSummary} />}
        {tab === "us_mileage" && <UsMileageTab onChange={loadSummary} />}
      </motion.div>
    </div>
  );
}

const SummaryTile = ({ label, value, hint, warn }) => (
  <div className={`glass rounded-xl p-5 ${warn ? "ring-1 ring-[#E63946]/40" : ""}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
    <div className={`mono text-2xl mt-2 ${warn ? "text-[#E63946]" : ""}`}>{value}</div>
    {hint ? <div className="text-xs text-white/45 mt-1">{hint}</div> : null}
  </div>
);

const CRO_RAG = { green: "#00A86B", amber: "#FFB020", red: "#E63946", acknowledged: "#00A86B" };
function ReminderBanner({ title, item, onManage, testid }) {
  const color = CRO_RAG[item.status] || "#00A86B";
  const dr = item.days_remaining;
  const text = dr < 0 ? `OVERDUE by ${Math.abs(dr)} days` : dr === 0 ? "due TODAY" : `due in ${dr} days (${item.weeks_remaining} weeks)`;
  return (
    <div className="rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3" style={{ background: `${color}14`, border: `1px solid ${color}55` }} data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
        <div className="text-sm">
          <span className="font-semibold" style={{ color }}>{title}</span>
          <span className="text-white/70"> — {text}. Filing deadline <span className="mono">{item.filing_deadline}</span></span>
        </div>
      </div>
      <button onClick={onManage} className="btn-ghost text-xs" data-testid={`${testid}-manage`}>Manage</button>
    </div>
  );
}
